import { useEffect, useRef, useState } from "react";
import { Download, MoreVertical, PlusSquare, Share, X } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { isPwaStandalone } from "@/lib/pwa";

const DISMISSED_KEY = "stockmusic_pwa_install_dismissed";

function getPlatform() {
  const ua = window.navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
  const isAndroid = /Android/.test(ua);
  return { isIOS, isAndroid };
}

export default function PWAInstallPrompt() {
  const { t } = useI18n();
  const [installEvent, setInstallEvent] = useState(null);
  const installEventRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [manualMode, setManualMode] = useState(false);

  useEffect(() => {
    setInstalled(isPwaStandalone());

    const onBeforeInstallPrompt = (event) => {
      event.preventDefault();
      installEventRef.current = event;
      setInstallEvent(event);
      if (localStorage.getItem(DISMISSED_KEY) !== "true" && !isPwaStandalone()) {
        setManualMode(false);
        setVisible(true);
      }
    };

    const onOpenInstallPrompt = () => {
      if (isPwaStandalone()) return;
      localStorage.removeItem(DISMISSED_KEY);
      setManualMode(!installEventRef.current);
      setVisible(true);
    };

    const onInstalled = () => {
      setInstalled(true);
      setVisible(false);
      installEventRef.current = null;
      setInstallEvent(null);
      localStorage.removeItem(DISMISSED_KEY);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstallPrompt);
    window.addEventListener("stockmusic:open-install-prompt", onOpenInstallPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstallPrompt);
      window.removeEventListener("stockmusic:open-install-prompt", onOpenInstallPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (!visible || installed) return null;

  const install = async () => {
    if (!installEvent) {
      setManualMode(true);
      return;
    }
    installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice?.outcome === "accepted") {
      setVisible(false);
    }
    installEventRef.current = null;
    setInstallEvent(null);
  };

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, "true");
    setVisible(false);
  };

  const { isIOS, isAndroid } = getPlatform();
  const showManual = manualMode || !installEvent;

  return (
    <div className="fixed bottom-24 left-4 right-4 z-[60] mx-auto max-w-xl rounded-2xl border border-[#D4A23A]/30 bg-zinc-950/95 p-4 text-white shadow-2xl shadow-black/50 backdrop-blur md:bottom-6 md:left-auto md:right-6 md:w-[440px]">
      <button onClick={dismiss} className="absolute right-3 top-3 rounded-full p-1 text-zinc-400 hover:bg-white/10 hover:text-white" aria-label={t("install.close")}>
        <X size={16} />
      </button>
      <div className="flex gap-3 pr-7">
        <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#D4A23A] text-black">
          <Download size={20} />
        </div>
        <div>
          <h3 className="font-semibold">{t("install.title")}</h3>
          <p className="mt-1 text-sm text-zinc-400">{t("install.subtitle")}</p>
          {showManual ? (
            <div className="mt-3 space-y-2 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-sm text-zinc-300">
              {isIOS ? (
                <>
                  <p className="flex items-center gap-2"><Share size={16} className="text-[#D4A23A]" /> {t("install.iosStep1")}</p>
                  <p className="flex items-center gap-2"><PlusSquare size={16} className="text-[#D4A23A]" /> {t("install.iosStep2")}</p>
                </>
              ) : isAndroid ? (
                <>
                  <p className="flex items-center gap-2"><MoreVertical size={16} className="text-[#D4A23A]" /> {t("install.androidStep1")}</p>
                  <p className="flex items-center gap-2"><Download size={16} className="text-[#D4A23A]" /> {t("install.androidStep2")}</p>
                </>
              ) : (
                <>
                  <p className="flex items-center gap-2"><MoreVertical size={16} className="text-[#D4A23A]" /> {t("install.desktopStep1")}</p>
                  <p className="flex items-center gap-2"><Download size={16} className="text-[#D4A23A]" /> {t("install.desktopStep2")}</p>
                </>
              )}
            </div>
          ) : (
            <button onClick={install} className="mt-3 rounded-full bg-[#D4A23A] px-4 py-2 text-sm font-semibold text-black hover:bg-[#E2B85C]">
              {t("install.action")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
